import { useMemo } from "react";
import { Space } from "antd";
import cuid from "cuid";
import { get, isPlainObject } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import {
  AGE_RULES,
  AGE_RULE_VALUES,
  DATE_BOUND_UNIT,
  FIELD_VALIDATION_LIMITS,
  TODAY_BOUND,
  VALIDATION_MESSAGE_KEYS,
  VALIDATORS,
  getValidatorsForInputType,
} from "utils/validation-rules";
import {
  DateBoundEditor,
  NumberField,
  buildDurationBound,
} from "./dateBoundControls";
import FieldConditionsEditor from "./FieldConditionsEditor";

const PARAM_FIELDS = {
  [VALIDATORS.REGEX]: ["pattern"],
  [VALIDATORS.MIN_LENGTH]: ["number"],
  [VALIDATORS.MAX_LENGTH]: ["number"],
  [VALIDATORS.MIN]: ["number"],
  [VALIDATORS.MAX]: ["number"],
  [VALIDATORS.DATE_MIN]: ["date"],
  [VALIDATORS.DATE_MAX]: ["date"],
  [VALIDATORS.MIN_AGE]: ["number", "ageRule"],
};

const PARAM_KEYS = ["pattern", "value", "ageRule"];

const flattenValidationMessages = (messages, prefix = "") => {
  if (!isPlainObject(messages)) return [];
  return Object.entries(messages).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      return [{ value: path, label: value }];
    }
    if (isPlainObject(value)) {
      return flattenValidationMessages(value, path);
    }
    return [];
  });
};

const FieldRulesEditor = ({
  rules = [],
  variables = [],
  labels = {},
  fieldType = "auto",
  onChange,
}) => {
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const validationMessages = tErrors("VALIDATIONS", { returnObjects: true });
  const messageKeyOptions = useMemo(() => {
    const options = flattenValidationMessages(validationMessages);
    return options.sort((left, right) =>
      String(left.label).localeCompare(String(right.label), undefined, {
        sensitivity: "base",
      }),
    );
  }, [validationMessages]);

  const validatorOptions = useMemo(
    () =>
      getValidatorsForInputType(fieldType).map((name) => ({
        value: name,
        label: get(labels, ["VALIDATORS", name], name),
      })),
    [fieldType, labels],
  );

  const ageRuleOptions = AGE_RULE_VALUES.map((value) => ({
    value,
    label: get(labels, ["AGE_RULE_OPTIONS", value], value),
  }));

  const updateRule = (index, updater) =>
    onChange(rules.map((rule, i) => (i === index ? updater(rule) : rule)));

  return (
    <Space direction="vertical" size={12} className="canvas-editor__full-width">
      {(rules || []).map((rule, index) => {
        const params = PARAM_FIELDS[rule.validator] || [];
        const ruleValidatorOptions =
          rule.validator &&
          !validatorOptions.some((option) => option.value === rule.validator)
            ? [
                {
                  value: rule.validator,
                  label: get(
                    labels,
                    ["VALIDATORS", rule.validator],
                    rule.validator,
                  ),
                },
                ...validatorOptions,
              ]
            : validatorOptions;
        const ruleMessageOptions =
          rule.messageKey &&
          !messageKeyOptions.some((option) => option.value === rule.messageKey)
            ? [
                { value: rule.messageKey, label: rule.messageKey },
                ...messageKeyOptions,
              ]
            : messageKeyOptions;
        return (
          <div key={rule.id || index} className="canvas-editor__panel">
            <div className="canvas-editor__row-between mb-8">
              <span className="canvas-editor__label-strong">
                {labels.RULE} {index + 1}
              </span>
              <div
                className="delete-icon"
                onClick={() => onChange(rules.filter((_, i) => i !== index))}
              >
                <BiTrash color={COLORS.C_DANGER} size={20} />
              </div>
            </div>
            <Select
              showSearch
              label={labels.VALIDATOR}
              placeholder={labels.VALIDATOR}
              options={ruleValidatorOptions}
              value={rule.validator || undefined}
              onChange={(validator) =>
                updateRule(index, (current) => {
                  const next = { ...current, validator };
                  PARAM_KEYS.forEach((key) => delete next[key]);
                  delete next.effectiveDateAccessor;
                  if (validator === VALIDATORS.MIN_AGE) {
                    next.ageRule = AGE_RULES.AGE_ON_EFFECTIVE_DATE;
                    next.value = FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE;
                  }
                  if (validator === VALIDATORS.DATE_MIN) {
                    next.value = buildDurationBound(
                      "-",
                      FIELD_VALIDATION_LIMITS.BIRTH_DATE_MIN_YEARS,
                      DATE_BOUND_UNIT.YEAR,
                    );
                  }
                  if (validator === VALIDATORS.DATE_MAX) {
                    next.value = { ...TODAY_BOUND };
                  }
                  return next;
                })
              }
            />
            {params.includes("pattern") ? (
              <Input
                label={labels.PATTERN}
                placeholder={labels.PATTERN}
                value={rule.pattern || ""}
                onChange={(event) =>
                  updateRule(index, (current) => ({
                    ...current,
                    pattern: event.target.value,
                  }))
                }
              />
            ) : null}
            {params.includes("number") ? (
              <NumberField
                label={
                  rule.validator === VALIDATORS.MIN_AGE
                    ? labels.MIN_AGE_VALUE
                    : labels.PARAM_VALUE
                }
                placeholder={
                  rule.validator === VALIDATORS.MIN_AGE
                    ? String(FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE)
                    : String(FIELD_VALIDATION_LIMITS.POSITIVE_NUMBER_MIN)
                }
                value={rule.value}
                onChange={(value) =>
                  updateRule(index, (current) => ({ ...current, value }))
                }
              />
            ) : null}
            {params.includes("date") ? (
              <DateBoundEditor
                label={
                  rule.validator === VALIDATORS.DATE_MIN
                    ? labels.MIN_DATE
                    : labels.MAX_DATE
                }
                labels={labels}
                allowNone={false}
                value={rule.value}
                defaultSign="-"
                defaultAmount={
                  rule.validator === VALIDATORS.DATE_MIN
                    ? FIELD_VALIDATION_LIMITS.BIRTH_DATE_MIN_YEARS
                    : TODAY_BOUND.amount
                }
                defaultUnit={
                  rule.validator === VALIDATORS.DATE_MIN
                    ? DATE_BOUND_UNIT.YEAR
                    : DATE_BOUND_UNIT.DAY
                }
                onChange={(value) =>
                  updateRule(index, (current) => ({ ...current, value }))
                }
              />
            ) : null}
            {params.includes("ageRule") ? (
              <>
                <Select
                  showSearch
                  label={labels.AGE_RULE}
                  placeholder={labels.AGE_RULE}
                  options={ageRuleOptions}
                  value={rule.ageRule || AGE_RULES.AGE_ON_EFFECTIVE_DATE}
                  onChange={(ageRule) =>
                    updateRule(index, (current) => ({
                      ...current,
                      ageRule,
                    }))
                  }
                />
                {rule.ageRule ? (
                  <p className="canvas-editor__meta">
                    {get(labels, ["AGE_RULE_DESCRIPTIONS", rule.ageRule], "")}
                  </p>
                ) : null}
                <p className="canvas-editor__meta">
                  {labels.EFFECTIVE_DATE_SOURCE}
                </p>
              </>
            ) : null}
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              label={labels.MESSAGE_KEY}
              placeholder={labels.MESSAGE_KEY}
              options={ruleMessageOptions}
              value={rule.messageKey || undefined}
              onChange={(messageKey) =>
                updateRule(index, (current) => ({
                  ...current,
                  messageKey: messageKey || undefined,
                }))
              }
            />
            <Input
              label={labels.MESSAGE}
              placeholder={labels.MESSAGE}
              value={rule.message || ""}
              onChange={(event) =>
                updateRule(index, (current) => ({
                  ...current,
                  message: event.target.value,
                }))
              }
            />
            <div className="canvas-editor__subsection">
              <div className="canvas-editor__section-title">{labels.WHEN}</div>
              <FieldConditionsEditor
                value={rule.when || []}
                variables={variables}
                labels={labels}
                groupMode="all"
                onChange={(when) =>
                  updateRule(index, (current) => ({
                    ...current,
                    when:
                      Array.isArray(when) && !when.length ? undefined : when,
                  }))
                }
              />
            </div>
          </div>
        );
      })}
      <Button
        type="primary"
        onClick={() =>
          onChange([
            ...(rules || []),
            {
              id: cuid(),
              validator: VALIDATORS.REQUIRED,
              messageKey: VALIDATION_MESSAGE_KEYS.REQUIRED,
            },
          ])
        }
      >
        <BiPlus size={20} /> {labels.ADD_RULE}
      </Button>
    </Space>
  );
};

FieldRulesEditor.propTypes = {
  rules: PropTypes.array,
  variables: PropTypes.array,
  labels: PropTypes.object,
  fieldType: PropTypes.string,
  onChange: PropTypes.func,
};

FieldRulesEditor.defaultProps = {
  rules: [],
  variables: [],
  labels: {},
  fieldType: "auto",
  onChange: undefined,
};

export default FieldRulesEditor;
